import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import AdPageLayout from '../components/AdPageLayout';
import { getAllPublicItineraries, getPublicTours } from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faFilter } from '@fortawesome/free-solid-svg-icons';
import PublicItineraryModal from '../components/PublicItineraryModal';
import TourModal from '../components/TourModal';
import FloatingTabs from '../components/FloatingTabs';
import useTranslation from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';

const Wrapper = styled.div`
  background: #f5f7f9;
  min-height: 100vh;
`;

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px 20px;
`;

const Title = styled.h1`
  color: #14464F;
  font-family: 'Josefin Sans', sans-serif;
  font-weight: 700;
  font-size: 30px;
  margin-bottom: 2rem;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(237.5px, 1fr));
  gap: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
  cursor: ${props => props.isSoldOut ? 'default' : 'pointer'};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 200px;
  position: relative;

  &:hover {
    transform: ${props => props.isSoldOut ? 'none' : 'translateY(-5px)'};
    box-shadow: ${props => props.isSoldOut ? '0 2px 4px rgba(0,0,0,0.1)' : '0 4px 8px rgba(0,0,0,0.2)'};
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 15px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding-top: 45px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0));
  color: white;
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  color: white;
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  text-align: left;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const CardDescription = styled.p`
  display: none;
`;

const Price = styled.div`
  color: #316C6E;
  font-weight: 600;
  font-size: 0.95rem;
  text-align: left;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #14464F;
  font-size: 14px;
  padding: 10px;
  background-color: #f8f8f8;
  border-top: 1px solid #eee;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: #14464F;
  font-size: 18px;
`;

const CardType = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  color: #14464F;
  font-weight: 500;
  ${props => props.isGuidedTour && `
    background-color: rgba(243, 133, 48, 0.9);
    color: white;
    padding: 4px 12px;
    cursor: pointer;
    backdrop-filter: blur(4px);
    transition: all 0.3s ease;
    
    &:hover {
      background-color: rgba(214, 116, 40, 0.95);
    }
  `}
`;

const PriceTag = styled.div`
  position: absolute;
  top: 15px;
  right: 10px;
  background-color: rgba(49, 108, 110, 0.9);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  color: white;
  font-weight: 500;
  backdrop-filter: blur(4px);
  z-index: 1;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  border: 2px solid #316C6E;
  background-color: ${props => props.active ? '#316C6E' : 'white'};
  color: ${props => props.active ? 'white' : '#316C6E'};
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: ${props => props.active ? '#254F51' : '#f0f1f2'};
  }
`;

const OperatorTag = styled(Link)`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: rgba(243, 133, 48, 0.9);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  color: white;
  font-weight: 500;
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  transition: all 0.3s ease;
  z-index: 1;

  &:hover {
    background-color: rgba(214, 116, 40, 0.95);
    color: white;
  }
`;

const SoldOutOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  &::after {
    content: '${props => props.soldOutText}';
    background: rgba(255, 255, 255, 0.9);
    color: #d32f2f;
    padding: 8px 24px;
    border-radius: 20px;
    font-weight: 600;
    font-size: 14px;
    transform: rotate(-15deg);
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

const ToursPage = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const history = useHistory();
  const [publicItineraries, setPublicItineraries] = useState([]);
  const [tours, setTours] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [selectedTour, setSelectedTour] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    guided: true,
    selfGuided: true
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Current language:', language);
        const itinerariesData = await getAllPublicItineraries({ language });
        console.log('Fetched itineraries:', itinerariesData);
        setPublicItineraries(itinerariesData);
        
        try {
          const toursData = await getPublicTours({ language });
          setTours(toursData || []);
        } catch (tourError) {
          console.error('Error fetching tours:', tourError);
          setTours([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setPublicItineraries([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [language]);

  const handleTabChange = (tab) => {
    if (tab === 'explored') {
      history.push('/');
    }
  };

  const getFirstImage = (imageUrl) => {
    try {
      const images = JSON.parse(imageUrl);
      return Array.isArray(images) ? images[0] : imageUrl;
    } catch {
      return imageUrl;
    }
  };

  const allItems = [
    ...publicItineraries.map(item => ({ ...item, type: 'itinerary' })),
    ...tours.map(item => ({ ...item, type: 'tour' }))
  ];

  const toggleFilter = (filter) => {
    setActiveFilters(prev => ({
      ...prev,
      [filter]: !prev[filter]
    }));
  };

  const filteredItems = allItems.filter(item => {
    if (item.type === 'tour' && !activeFilters.guided) return false;
    if (item.type === 'itinerary' && !activeFilters.selfGuided) return false;
    return true;
  });

  useEffect(() => {
    if (filteredItems.length > 0) {
      console.log('Filtered items:', filteredItems.map(item => ({
        id: item.id,
        type: item.type,
        name: item.name,
        name_es: item.name_es,
        language
      })));
    }
  }, [filteredItems, language]);

  const isSoldOut = (item) => {
    if (item.type !== 'tour') return false;
    
    // If there are no dates, it's sold out
    if (!item.dates || item.dates.length === 0) return true;
    
    // If all dates have current_stock as 0, it's sold out
    return item.dates.every(date => date.current_stock === 0);
  };

  return (
    <Wrapper>
      <Navbar hideFilters />
      <AdPageLayout>
        <PageContainer>
          <Title>{t('pages.tours.title')}</Title>
          
          <FilterContainer>
            <FilterButton 
              active={activeFilters.guided}
              onClick={() => toggleFilter('guided')}
            >
              <FontAwesomeIcon icon={faFilter} />
              {t('pages.tours.filters.guidedTours')}
            </FilterButton>
            <FilterButton 
              active={activeFilters.selfGuided}
              onClick={() => toggleFilter('selfGuided')}
            >
              <FontAwesomeIcon icon={faFilter} />
              {t('pages.tours.filters.selfGuided')}
            </FilterButton>
          </FilterContainer>

          {isLoading ? (
            <LoadingSpinner>{t('pages.tours.loading')}</LoadingSpinner>
          ) : filteredItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              {t('pages.tours.noResults')}
            </div>
          ) : (
            <CardGrid>
              {filteredItems.map((item) => (
                <Card 
                  key={`${item.type}-${item.id}`} 
                  isSoldOut={isSoldOut(item)}
                  onClick={() => {
                    if (isSoldOut(item)) return;
                    if (item.type === 'itinerary') {
                      setSelectedItinerary(item);
                    } else {
                      setSelectedTour(item);
                    }
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <CardImage 
                      src={getFirstImage(item.image_url)} 
                      alt={item.name} 
                    />
                    {isSoldOut(item) && (
                      <SoldOutOverlay 
                        soldOutText={t('components.tourModal.soldOut')}
                      />
                    )}
                    <CardType 
                      isGuidedTour={item.type === 'tour'}
                      onClick={(e) => {
                        if (item.type === 'tour' && item.TourOperator) {
                          e.stopPropagation();
                          history.push(`/operators/${item.operatorId}/profile`);
                        }
                      }}
                    >
                      {item.type === 'tour' && item.TourOperator 
                        ? `${t('pages.tours.cardTypes.guidedTour')} ${t('pages.tours.operatorTag')} ${item.TourOperator.name}`
                        : t('pages.tours.cardTypes.selfGuided')
                      }
                    </CardType>
                  </div>
                  <CardContent>
                    {item.type === 'tour' && (
                      <PriceTag>
                        ${Number(item.price).toLocaleString()} DOP
                      </PriceTag>
                    )}
                    <CardTitle>
                      {item.type === 'itinerary' 
                        ? (language === 'es' && item.name_es ? item.name_es : item.name)
                        : item.name}
                    </CardTitle>
                    <CardDescription>
                      {item.type === 'itinerary'
                        ? (language === 'es' && item.description_es ? item.description_es : item.description)
                        : item.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </CardGrid>
          )}
        </PageContainer>
        <FloatingTabs onTabChange={handleTabChange} activeTab="tours" />
        
        {selectedItinerary && (
          <PublicItineraryModal
            itinerary={selectedItinerary}
            onClose={() => setSelectedItinerary(null)}
            open={!!selectedItinerary}
          />
        )}
        
        {selectedTour && (
          <TourModal
            tour={selectedTour}
            onClose={() => setSelectedTour(null)}
            open={!!selectedTour}
          />
        )}
      </AdPageLayout>
    </Wrapper>
  );
};

export default ToursPage; 